<!-- Create a twin table and take care of data loading -->
<template lang="pug">
TwinTable(
  :id="props.id",
  :loading="loading",
  :assets="assets",
  :properties="twinProperties",
  :page="paging.page",
  :page-count="pagesTotal",
  :hide-delete="props.hideDelete",
  :property-ids="props.propertyIds",
  :show-created="props.showCreated",
  :default-sort="defaultSort",
  @sort="onSort",
  @row-click="onRowClick",
  @page="(page) => (paging.page = page)",
  @delete="deleteTwin"
)
  //- Forward named slots
  template(v-for="(__, name) in $slots", #[name]="slotData")
    slot(:name="name", v-bind="slotData")
</template>

<script setup lang="ts">
import _, { debounce } from "lodash";
import {
  getCurrentInstance,
  inject,
  onMounted,
  ref,
  watch,
  computed,
} from "vue";
import {
  type TwinProperty,
  type TwinQuery,
  searchTwins,
  type TwinResponse,
} from "@shared/services/twinManagement";
import { useTwinStore } from "../../../stores/twinManagement";
import {
  type PaginatedLoadState,
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
} from "../../../models/state";
import TwinTable from "@shared/components/twinManagement/components/TwinTable.vue";
import type { PaginationQuery } from "../../../services/common";

const props = defineProps<{
  id: string;
  search?: string;
  hideDelete?: boolean;
  query?: TwinQuery;
  // NOTE: the template to determine the tablke columns, multiple tempaltes are supported using templateQuery
  templateId?: string;
  // NOTE: multiple templates are supported, but currently limited to 20 due to paging
  // These templates are being used to determine the table header, and are requested using the templateQuery
  // If templateId is set, this is being ignored.
  templateQuery?: TwinQuery;
  propertyIds?: string[];
  // trigger search instantly without debounce
  instantSearch?: string;
  // Show created field of the twin, last in the table
  showCreated?: boolean;
}>();

const emit = defineEmits(["rowClick", "delete", "templates", "total"]);
const instance = getCurrentInstance();

type Sorting = {
  order: "asc" | "desc";
  sortBy: { customId: string; type: string };
};

const defaultSorting = inject<Pick<Sorting, "order"> & { customId: string }>(
  `${props.id}.defaultSort`,
);
const sorting = ref<Sorting | null>(null);
const defaultSort = ref<
  { prop: string; order: "ascending" | "descending" } | undefined
>();

function onSort(sortOrder: Sorting | null) {
  sorting.value = sortOrder;
  void loadData();
}

watch(
  () => props.query,
  (newVal, oldVal) => {
    if (!_.isEqual(oldVal, newVal)) {
      void loadData();
    }
  },
);

const twinManagementStore = useTwinStore();

const twinsLoadingState = computed(() =>
  twinManagementStore.twinTables.get(props.id),
);

async function loadTwins(paging: PaginationQuery, query: TwinQuery) {
  const page = {
    page: paging ? paging.page : 1,
    pageSize: paging ? paging.pageSize : 20,
  };

  const params = {
    isTemplate: false,
    showProperties: true,
    active: true,
    ...query,
  };

  await twinManagementStore.loadTwinTable(
    props.id,
    { page, query: params },
    false,
  );
}

const loading = computed(() => twinsLoadingState.value?.loading);
const assets = computed(() => twinsLoadingState.value?.result || []);

const pagesTotal = computed(() => twinsLoadingState.value?.pagesTotal || 0);

function loadData() {
  const query: TwinQuery = {
    search: props.search !== "" ? props.search : undefined,
    ...props.query,
  };
  if (sorting.value !== null) {
    query["sortOrder"] = sorting.value.order;
    query["sortBy"] = {
      property: sorting.value.sortBy,
    };
  }
  return loadTwins(paging.value, query);
}

defineExpose({ reload: loadData });

const debouncedReload = debounce(loadData, 500);

const paging = ref({
  page: 1,
  pageSize: 10,
});

watch(paging.value, () => {
  void loadData();
});

// NOTE: we allow using multiple templates as a source to determine the header of the table
const templatesLoadingState = ref<PaginatedLoadState<TwinResponse[]>>({
  ...stateInitial,
  query: null,
  result: [],
});

async function loadTwinTemplates() {
  const page = {
    page: 1,
    pageSize: 20,
  };

  const query = props.templateId
    ? { uuid: props.templateId }
    : props.templateQuery;

  const params = {
    isTemplate: true,
    showProperties: true,
    active: true,
    ...query,
  };

  templatesLoadingState.value = {
    ...templatesLoadingState.value,
    ...stateLoading,
    query,
  };

  const response = await searchTwins(page, params).catch((e) => {
    templatesLoadingState.value = {
      ...templatesLoadingState.value,
      ...stateError,
    };
    throw e;
  });

  templatesLoadingState.value = {
    ...stateLoaded,
    result: response.data.result,
    query: params,
    page: {
      pageSize: page.pageSize,
      // @ts-expect-error backend uses page here
      currentPage: response.data.page,
      pagesTotal: response.data.pagesTotal,
      total: response.data.total,
    },
  };

  emit("templates", templatesLoadingState.value.result);
}

const twinProperties = computed<TwinProperty[]>(() => {
  const properties = (templatesLoadingState.value.result ?? []).flatMap(
    (item) => item.properties ?? [],
  );
  return properties
    .filter(
      (item, pos) =>
        properties.findIndex((sItem) => sItem.name === item.name) === pos,
    )
    .sort((a, b) => Number(a.order) - Number(b.order));
});

function initializeDefaultSort() {
  const property = twinProperties.value.find(
    (item) => item.customId === defaultSorting?.customId,
  );
  if (defaultSorting === undefined || property === undefined) {
    return;
  }

  sorting.value = {
    order: defaultSorting.order,
    sortBy: { customId: defaultSorting.customId, type: property.type },
  };
  defaultSort.value = {
    prop: property.name,
    order: defaultSorting.order === "asc" ? "ascending" : "descending",
  };
}

async function initialize() {
  await loadTwinTemplates();
  initializeDefaultSort();
  await loadData();
}

onMounted(() => {
  if (!props.hideDelete && instance?.vnode.props?.onDelete === undefined) {
    console.warn(
      "[TwinTableDataLoading] 'delete' handler is not set. Set @delete or hide the delete action with :hide-delete=\"true\".",
    );
  }
  void initialize();
});

// Search
watch(() => props.search, debouncedReload);
watch(() => props.instantSearch, loadData);

// Actions

function onRowClick(twin: TwinResponse) {
  emit("rowClick", twin);
}

function deleteTwin(twin: TwinResponse) {
  emit("delete", twin);
}
</script>
